import React, { useState, useEffect } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { Shield, Users, UserX, AlertTriangle, LogOut, RefreshCw, Mail, User, Calendar, Edit2, ChevronDown, ChevronUp } from 'lucide-react';
import './SettingsTab.css';

const SettingsTab = ({ user: activeUser, analysisResult, onRestart, setActiveTab }) => {
    const { signOut } = useClerk();
    
    // Account edit state
    const [isEditing, setIsEditing] = useState(false);
    const [accountData, setAccountData] = useState({
        name: '',
        email: '',
        age: ''
    });

    // Danger Zone visibility state
    const [showDangerZone, setShowDangerZone] = useState(false);

    // Connections state
    const [connections, setConnections] = useState([]);
    const [isLoadingConnections, setIsLoadingConnections] = useState(true);

    useEffect(() => {
        // Robust extraction from Clerk object
        const fullName = activeUser?.fullName || `${activeUser?.firstName || ''} ${activeUser?.lastName || ''}`.trim();
        const primaryEmail = activeUser?.primaryEmailAddress?.emailAddress || activeUser?.emailAddresses?.[0]?.emailAddress || '';
        
        // Robust extraction of age from Clerk or analysis result DB schema
        const age = activeUser?.unsafeMetadata?.age || analysisResult?.age || analysisResult?.metadata?.age || analysisResult?.cards_data?.age || '';

        setAccountData({
            name: fullName || '',
            email: primaryEmail || '',
            age: age
        });
    }, [activeUser, analysisResult]);

    useEffect(() => {
        if (activeUser?.id) {
            setIsLoadingConnections(true);
            const clerkId = activeUser.id;
            const backendId = activeUser?.unsafeMetadata?.backend_id || clerkId;
            
            const url = `/api/invites/user/${backendId}?clerk_id=${clerkId}`;
            
            fetch(url)
                .then(res => res.json())
                .then(data => {
                    if (data.success && data.invites) {
                        const seen = new Set();
                        const accepted = data.invites.filter(i => {
                            const isLinked = i.status === 'accepted' || i.status === 'completed';
                            if (isLinked && !seen.has(i.id)) {
                                seen.add(i.id);
                                return true;
                            }
                            return false;
                        });
                        setConnections(accepted);
                    }
                })
                .catch(err => console.error("Failed to load connections in Settings", err))
                .finally(() => setIsLoadingConnections(false));
        }
    }, [activeUser]);

    const handleSaveAccount = async () => {
        try {
            if (activeUser) {
                const parts = accountData.name.trim().split(' ');
                const firstName = parts[0] || '';
                const lastName = parts.slice(1).join(' ') || '';
                
                await activeUser.update({
                    firstName: firstName,
                    lastName: lastName,
                    unsafeMetadata: {
                        ...activeUser.unsafeMetadata,
                        age: accountData.age
                    }
                });
                await activeUser.reload();
            }
            setIsEditing(false);
        } catch (err) {
            console.error("Error updating profile", err);
            alert("Failed to update profile: " + (err.errors?.[0]?.message || 'Unknown error'));
        }
    };

    const getDisplayName = (inv) => {
        if (inv._connected_name) return inv._connected_name;
        if (inv.invited_email && !['link-invite', 'link-invite-accepted'].includes(inv.invited_email)) return inv.invited_email;
        return inv._perspective === 'received' ? 'Synced Friend' : 'Linked Profile';
    };

    const getAvatar = (inv) => {
        const name = getDisplayName(inv);
        return /^[A-Za-z]/.test(name) ? name.charAt(0).toUpperCase() : '🔗';
    };

    const handleDisconnect = async (inviteId) => {
        if (!window.confirm("Are you sure you want to remove this connection? They will no longer see your data, and you will no longer see theirs.")) return;
        
        try {
            const clerkId = activeUser?.id;
            const backendId = activeUser?.unsafeMetadata?.backend_id || clerkId;
            const endpoint = `/api/invites/${inviteId}`;
            
            const response = await fetch(endpoint, { 
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: backendId, clerk_id: clerkId })
            });
            const data = await response.json();
            
            if (data.success) {
                setConnections(connections.filter(c => c.id !== inviteId));
            } else {
                alert(`Failed to remove connection: ${data.error}`);
            }
        } catch (err) {
            console.error(err);
            alert("An error occurred while removing the connection.");
        }
    };

    const handleDeleteAccount = async () => {
        if (window.confirm("Are you absolutely sure? This action cannot be undone and all your handwriting analysis data will be permanently deleted.")) {
            try {
                const clerkId = activeUser?.id;
                const backendId = activeUser?.unsafeMetadata?.backend_id || clerkId;
                
                // 1. Delete all user data from the Supabase backend
                const response = await fetch(`/api/users/${backendId}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ clerk_id: clerkId })
                });
                
                const data = await response.json();
                
                if (data.success) {
                    // 2. Delete the user from Clerk (this automatically logs them out)
                    try {
                        await activeUser.delete();
                        alert("Your account has been successfully deleted.");
                    } catch (clerkErr) {
                        console.error("Clerk deletion failed, but DB data was deleted:", clerkErr);
                        alert("Your personal data was deleted, but your login account could not be removed automatically. Please contact support if you wish to fully remove your login.");
                    }
                } else {
                    alert(`Failed to delete account data: ${data.error}`);
                }
            } catch (err) {
                console.error("Error deleting account:", err);
                alert("An error occurred while deleting your account.");
            }
        }
    };

    return (
        <div className="settings-tab-container">
            <div className="settings-content">
                
                {/* 1. PERSONAL DETAILS SECTION (iOS Style Profile Card) */}
                <section className="profile-card-section">
                    <div className="profile-header">
                        {activeUser?.imageUrl ? (
                            <img src={activeUser.imageUrl} alt="Profile Avatar" className="profile-avatar" />
                        ) : (
                            <div className="profile-avatar-placeholder">
                                <User size={32} />
                            </div>
                        )}
                        <h2 className="profile-name-title">{accountData.name || 'Your Profile'}</h2>
                        {!isEditing && (
                            <button className="icon-edit-btn" onClick={() => setIsEditing(true)}>
                                <Edit2 size={16} /> Edit
                            </button>
                        )}
                    </div>

                    <div className="profile-details-list">
                        <div className="profile-list-item">
                            <div className="list-icon"><User size={18} /></div>
                            <div className="list-content">
                                <label>Name</label>
                                {isEditing ? (
                                    <input 
                                        type="text" 
                                        value={accountData.name} 
                                        onChange={(e) => setAccountData({...accountData, name: e.target.value})}
                                        className="sleek-input"
                                        placeholder="Enter your name"
                                    />
                                ) : (
                                    <span className="list-value">{accountData.name || 'Not provided'}</span>
                                )}
                            </div>
                        </div>

                        <div className="profile-list-item">
                            <div className="list-icon"><Mail size={18} /></div>
                            <div className="list-content">
                                <label>Email Address</label>
                                {isEditing ? (
                                    <input 
                                        type="email" 
                                        value={accountData.email} 
                                        onChange={(e) => setAccountData({...accountData, email: e.target.value})}
                                        className="sleek-input"
                                        placeholder="Enter your email"
                                        disabled={true} /* Often emails can't be easily changed without verification */
                                    />
                                ) : (
                                    <span className="list-value">{accountData.email || 'Not provided'}</span>
                                )}
                            </div>
                        </div>

                        <div className="profile-list-item no-border">
                            <div className="list-icon"><Calendar size={18} /></div>
                            <div className="list-content">
                                <label>Age</label>
                                {isEditing ? (
                                    <input 
                                        type="number" 
                                        value={accountData.age} 
                                        onChange={(e) => setAccountData({...accountData, age: e.target.value})}
                                        className="sleek-input"
                                        placeholder="Enter your age"
                                    />
                                ) : (
                                    <span className="list-value">{accountData.age || 'Not provided'}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {isEditing && (
                        <div className="profile-edit-actions">
                            <button className="settings-btn cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                            <button className="settings-btn save-btn" onClick={handleSaveAccount}>Save Changes</button>
                        </div>
                    )}
                </section>

                {/* 2. PRIVACY & CONNECTIONS SECTION */}
                <section className="settings-section glass-panel">
                    <h3 className="section-title"><Shield size={18} /> Privacy & Sharing</h3>
                    <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '16px' }}>
                        Manage who can see your personality insights.
                    </p>

                    <div className="privacy-connections-list">
                        {isLoadingConnections ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>
                                Loading connections...
                            </div>
                        ) : connections.length > 0 ? (
                            connections.map((inv, idx) => (
                                <div key={inv.id || idx} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '12px', background: 'rgba(255,255,255,0.03)', 
                                    borderRadius: '12px', marginBottom: '8px', border: '1px solid rgba(255,255,255,0.05)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ 
                                            width: '32px', height: '32px', borderRadius: '50%', 
                                            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '14px', fontWeight: 'bold', color: 'white'
                                        }}>
                                            {getAvatar(inv)}
                                        </div>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'white' }}>
                                            {getDisplayName(inv)}
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => handleDisconnect(inv.id)}
                                        style={{
                                            background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)',
                                            color: '#ef4444', borderRadius: '8px', padding: '6px 12px',
                                            fontSize: '11px', fontWeight: 'bold', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '6px'
                                        }}
                                    >
                                        <UserX size={12} /> Remove
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>
                                No active connections to manage.
                            </div>
                        )}
                    </div>
                </section>

                {/* 3. ADVANCED OPTIONS (COLLAPSED DANGER ZONE) */}
                <div className="advanced-options-container">
                    <button 
                        className="advanced-options-toggle" 
                        onClick={() => setShowDangerZone(!showDangerZone)}
                    >
                        {showDangerZone ? 'Hide Advanced Options' : 'Show Advanced Options'}
                        {showDangerZone ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>

                    {showDangerZone && (
                        <section className="settings-section danger-zone animate-slide-down">
                            <div className="danger-header">
                                <AlertTriangle size={20} className="danger-icon" />
                                <h3 className="section-title text-red">Danger Zone</h3>
                            </div>
                            
                            {process.env.REACT_APP_ALLOW_USER_RETEST === 'true' && (
                                <div style={{ marginBottom: '16px' }}>
                                    <p className="danger-description" style={{ marginBottom: '8px' }}>
                                        Retaking the test will override your current traits.
                                    </p>
                                    <button className="settings-btn secondary-btn action-btn full-width" onClick={onRestart}>
                                        <RefreshCw size={16} /> Retake Initial Test
                                    </button>
                                </div>
                            )}

                            <p className="danger-description">
                                Deleting your account will permanently remove all your handwriting analysis, coach interactions, and connection data.
                            </p>
                            <button className="settings-btn danger-btn" onClick={handleDeleteAccount}>
                                Delete Account Data
                            </button>
                        </section>
                    )}
                </div>

            </div>
        </div>
    );
};

export default SettingsTab;
